import {
  AT_HOME_SITUATION,
  DOCS_FORM_NAMES,
  GUARDIAN,
  HOME_CARE_SITUATION,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  REHAB_FORM_NAMES,
} from "core/consts";
import { getUnixTime } from "date-fns";
import { ControlledInput, ControlledTextarea } from "ds/ui";
import { FormFieldConfig } from "..";
import {
  ControlledCheckboxGroupWithFocus,
  ControlledDatePickerWithFocus,
  ControlledRadioGroupWithFocus,
  SourceWrapper,
} from "../../utils";
import {
  CommunicationInGermanPossibleField,
  PatientPostfachField,
  PatientStreetField,
  PreferredClinicContactedField,
} from "./fields";

export const GKVGeneralFormSections: FormFieldConfig<
  typeof DOCS_FORM_NAMES.GKV_GENERAL
>["sections"] = [
  {
    fields: {
      insurance_number: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.insuredPerson
                .insuranceNumber
            }
            name="insurance_number"
          />
        ),
        position: 10,
      },
      patient_birthdate: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDatePickerWithFocus
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.insuredPerson.dateOfBirth
            }
            maxValue={getUnixTime(new Date())}
            name="patient_birthdate"
          />
        ),
        position: 9,
      },
      patient_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.city}
            name="patient_city"
          />
        ),
        position: 7,
      },
      patient_first_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.people.firstName}
            name="patient_first_name"
          />
        ),
        position: 1,
      },
      patient_housenumber: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.number}
            name="patient_housenumber"
          />
        ),
        position: 4,
      },
      patient_last_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.people.lastName}
            name="patient_last_name"
          />
        ),
        position: 2,
      },
      patient_phone_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.insuredPerson.phone}
            name="patient_phone_number"
            type="tel"
          />
        ),
        position: 8,
      },
      patient_postfach: {
        input: PatientPostfachField,
        position: 5,
      },
      patient_street: {
        input: PatientStreetField,
        position: 3,
      },
      patient_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.zipcode}
            name="patient_zipcode"
          />
        ),
        position: 6,
      },
    },
    position: 1,
    title: ({ translations }) =>
      translations.patientForms.generalForm.sectionI.titleDocs,
  },
  {
    fields: {
      domestic_situation_is_safe: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .currentHomeCareEnsured
              }
              name="domestic_situation_is_safe"
              options={options}
              size="md"
            />
          );
        },
        position: 10,
      },
      general_practitioner_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.sectionI.gpCity}
            name="general_practitioner_city"
            placeholder={
              translations.patient.patientInformation
                .generalPractitionerCityPlaceholder
            }
          />
        ),
        position: 18,
      },
      general_practitioner_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.firstName}
            name="general_practitioner_first_name"
            placeholder={
              translations.patient.patientInformation
                .generalPractitionerFirstNamePlaceholder
            }
          />
        ),
        position: 12,
      },
      general_practitioner_honorific_prefix: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patient.patientInformation
                .generalPractitionerHonorificPrefix
            }
            name="general_practitioner_honorific_prefix"
            placeholder={
              translations.patient.patientInformation
                .generalPractitionerHonorificPrefixPlaceholder
            }
          />
        ),
        position: 11,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI.questionGp,
        },
      },
      general_practitioner_honorific_suffix: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patient.patientInformation
                .generalPractitionerHonorificSuffix
            }
            name="general_practitioner_honorific_suffix"
            placeholder={
              translations.patient.patientInformation
                .generalPractitionerHonorificSuffixPlaceholder
            }
          />
        ),
        position: 11.5,
      },
      general_practitioner_housenumber: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.number}
            name="general_practitioner_housenumber"
            placeholder={
              translations.patient.patientInformation
                .generalPractitionerHouseNumberPlaceholder
            }
          />
        ),
        position: 16,
      },
      general_practitioner_job_title: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patient.patientInformation
                .generalPractitionerJobTitle
            }
            name="general_practitioner_job_title"
            placeholder={
              translations.patient.patientInformation
                .generalPractitionerJobTitlePlaceholder
            }
          />
        ),
        position: 14,
      },
      general_practitioner_last_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.lastName}
            name="general_practitioner_last_name"
            placeholder={
              translations.patient.patientInformation
                .generalPractitionerLastNamePlaceholder
            }
          />
        ),
        position: 13,
      },
      general_practitioner_phone_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.sectionI.gpPhoneNumber}
            name="general_practitioner_phone_number"
            placeholder={
              translations.patient.patientInformation
                .generalPractitionerPhonePlaceholder
            }
            type="tel"
          />
        ),
        position: 19,
      },
      general_practitioner_street: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.street}
            name="general_practitioner_street"
            placeholder={
              translations.patient.patientInformation
                .generalPractitionerStreetPlaceholder
            }
          />
        ),
        position: 15,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI.subtitleAddressGp,
        },
      },
      general_practitioner_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.sectionI.gpZipCode}
            name="general_practitioner_zipcode"
            placeholder={
              translations.patient.patientInformation
                .generalPractitionerZipcodePlaceholder
            }
          />
        ),
        position: 17,
      },
      insured_details_application_insurance_benefits: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .questionNeedForCare
              }
              name="insured_details_application_insurance_benefits"
              options={options}
              size="md"
            />
          );
        },
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI.titleNeedForCare,
        },
      },
      insured_details_guardian_1_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.city}
            name="insured_details_guardian_1_city"
          />
        ),
        position: 26,
      },
      insured_details_guardian_1_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.firstName}
            name="insured_details_guardian_1_first_name"
          />
        ),
        position: 21,
      },
      insured_details_guardian_1_house_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.number}
            name="insured_details_guardian_1_house_number"
          />
        ),
        position: 24,
      },
      insured_details_guardian_1_last_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.lastName}
            name="insured_details_guardian_1_last_name"
          />
        ),
        position: 22,
      },
      insured_details_guardian_1_phone: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.sectionI.gpPhoneNumber}
            name="insured_details_guardian_1_phone"
            type="tel"
          />
        ),
        position: 27,
      },
      insured_details_guardian_1_street: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.street}
            name="insured_details_guardian_1_street"
          />
        ),
        position: 23,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI.subtitleAddressGp,
        },
      },
      insured_details_guardian_1_type: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.patientForms.generalForm.sectionI.optionCarer,
              value: GUARDIAN.CARER,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI
                  .optionHealthCareRep,
              value: GUARDIAN.HEALTH_CARE_REP,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI.optionLegalRep,
              value: GUARDIAN.LEGAL_REP,
            },
            {
              label:
                translations.ontologies.guardianType.values
                  .spousalEmergencyRepresentative,
              value: GUARDIAN.SPOUSAL_EMERGENCY_REP,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              aria-label={
                translations.patientForms.generalForm.sectionI.questionGuardian
              }
              name="insured_details_guardian_1_type"
              options={options}
              size="md"
            />
          );
        },
        position: 20,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI.questionGuardian,
        },
      },
      insured_details_guardian_1_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.zipcode}
            name="insured_details_guardian_1_zipcode"
          />
        ),
        position: 25,
      },
      insured_details_guardian_2_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.city}
            name="insured_details_guardian_2_city"
          />
        ),
        position: 33,
      },
      insured_details_guardian_2_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.firstName}
            name="insured_details_guardian_2_first_name"
          />
        ),
        position: 28,
      },
      insured_details_guardian_2_house_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.number}
            name="insured_details_guardian_2_house_number"
          />
        ),
        position: 31,
      },
      insured_details_guardian_2_last_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.lastName}
            name="insured_details_guardian_2_last_name"
          />
        ),
        position: 29,
      },
      insured_details_guardian_2_phone: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.sectionI.gpPhoneNumber}
            name="insured_details_guardian_2_phone"
            type="tel"
          />
        ),
        position: 34,
      },
      insured_details_guardian_2_street: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.street}
            name="insured_details_guardian_2_street"
          />
        ),
        position: 30,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI.subtitleAddressGp,
        },
      },
      insured_details_guardian_2_type: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.patientForms.generalForm.sectionI.optionCarer,
              value: GUARDIAN.CARER,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI
                  .optionHealthCareRep,
              value: GUARDIAN.HEALTH_CARE_REP,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI.optionLegalRep,
              value: GUARDIAN.LEGAL_REP,
            },
            {
              label:
                translations.ontologies.guardianType.values
                  .spousalEmergencyRepresentative,
              value: GUARDIAN.SPOUSAL_EMERGENCY_REP,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              aria-label={
                translations.patientForms.generalForm.sectionI.questionGuardian
              }
              name="insured_details_guardian_2_type"
              options={options}
              size="md"
            />
          );
        },
        position: 27,
      },
      insured_details_guardian_2_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.zipcode}
            name="insured_details_guardian_2_zipcode"
          />
        ),
        position: 32,
      },
      living_lift: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .livingSituationElevator
              }
              name="living_lift"
              options={options}
              size="md"
            />
          );
        },
        position: 7,
      },
      living_lift_description: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI
                .livingSituationFloor
            }
            name="living_lift_description"
          />
        ),
        position: 6,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI
              .questionLivingSituation,
        },
      },
      living_situation: {
        input: ({
          formName,
          skipRequiredValidation,
          sourceInformation,
          translations,
        }) => {
          const homeOptions = [
            {
              label:
                translations.patientForms.generalForm.sectionI
                  .optionAtHomeAlone,
              value: AT_HOME_SITUATION.ALONE,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI
                  .optionAtHomeWithRelatives,
              value: AT_HOME_SITUATION.RELATIVES,
            },
          ];

          const homeCareSituationOptions = [
            {
              label:
                translations.patientForms.generalForm.sectionI.optionAtHome,
              subForm: () => (
                <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
                  fieldName="living_situation_at_home"
                  formName={formName}
                  isIconOnly
                  sourceInformation={sourceInformation}
                >
                  <ControlledRadioGroupWithFocus
                    aria-label={
                      translations.patientForms.generalForm.sectionI
                        .accessibilityAtHomeSituation
                    }
                    name="living_situation_at_home"
                    options={homeOptions}
                    size="md"
                  />
                </SourceWrapper>
              ),
              value: HOME_CARE_SITUATION.HOME,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI
                  .optionAssistedLiving,
              value: HOME_CARE_SITUATION.ASSISTED_LIVING,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI.optionCareHome,
              value: HOME_CARE_SITUATION.CARE_HOME,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI.subtitleHomeCare
              }
              name="living_situation"
              options={homeCareSituationOptions}
              size="md"
            />
          );
        },
        position: 9,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI.questionHomeCare,
        },
      },
      living_special: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.generalForm.sectionI
                .livingSituationSpecifications
            }
            name="living_special"
          />
        ),
        position: 8,
      },
      reason_for_reha_clinic: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.generalForm.sectionI
                .labelSpecialRequirements
            }
            name="reason_for_reha_clinic"
          />
        ),
        position: 35,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI
              .questionSpecialRequirements,
        },
      },
      retirement_earning_capacity_company: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.generalForm.sectionI
                .placeholderLabelInsuranceName
            }
            name="retirement_earning_capacity_company"
          />
        ),
        position: 5,
      },
      retirement_pension_applied: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .subtitleRetirementPension
              }
              name="retirement_pension_applied"
              options={options}
              size="md"
            />
          );
        },
        position: 2,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI
              .titlePensionApplication,
        },
      },
      retirement_pension_company: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.generalForm.sectionI
                .placeholderLabelInsuranceName
            }
            name="retirement_pension_company"
          />
        ),
        position: 3,
      },
      retirement_pension_earning_capacity_applied: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .subtitlePensionReducedEarning
              }
              name="retirement_pension_earning_capacity_applied"
              options={options}
              size="md"
            />
          );
        },
        position: 4,
      },
    },
    position: 2,
    title: ({ translations }) =>
      translations.patientForms.generalForm.sectionI.title,
  },
  {
    fields: {
      declaration_of_intent_confirmed: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledCheckboxGroupWithFocus
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI
                .signatureDeclaration
            }
            name="declaration_of_intent_confirmed"
            options={[
              {
                label:
                  translations.patientForms.generalForm
                    .declarationOfIntentProvided,
                value: PREDICAMENT_STATE_YES,
              },
            ]}
            size="md"
          />
        ),
        position: 1,
      },
    },
    position: 3,
    title: ({ translations }) =>
      translations.patientForms.generalForm.sectionI.additionalInfoNote,
  },
  {
    description: ({ translations }) =>
      translations.patientForms.generalForm.sectionI.consentToDataTransferText,
    fields: {
      data_transfer_consent_given: {
        input: ({ ariaLabelledById, skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.patientForms.generalForm.consentDataProvided,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label:
                translations.patientForms.generalForm.sectionTwo
                  .consentDataNotProvided,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroupWithFocus
              aria-labelledby={ariaLabelledById}
              isRequired={!skipRequiredValidation}
              name="data_transfer_consent_given"
              options={options}
              size="md"
            />
          );
        },
        position: 1,
        subTitle: {
          className: "font-normal! text-body-md!",
          isRequired: true,
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionI
              .consentToDataTransferSignature,
        },
      },
    },
    position: 4,
    title: ({ translations }) =>
      translations.patientForms.generalForm.sectionI
        .consentToDataTransferHeading,
  },
  {
    fields: {
      case_id: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.generalForm.sectionTwo.patientId}
            name="case_id"
          />
        ),
        position: 2,
      },
      communication_in_german_possible: {
        input: CommunicationInGermanPossibleField,
        position: 12,
        subTitle: {
          isRequired: true,
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionTwo
              .communicationWithPatient,
        },
      },
      hospital_city: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.address.city}
            name="hospital_city"
          />
        ),
        position: 7,
      },
      hospital_house_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.number}
            name="hospital_house_number"
          />
        ),
        position: 5,
      },
      hospital_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.institution.name}
            name="hospital_name"
          />
        ),
        position: 3,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionTwo.hospitalName,
        },
      },
      hospital_postal_code: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.address.zipcode}
            name="hospital_postal_code"
          />
        ),
        position: 6,
      },
      hospital_street: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.address.street}
            name="hospital_street"
          />
        ),
        position: 4,
      },
      prefered_clinic_contacted: {
        input: PreferredClinicContactedField,
        position: 13,
        subTitle: {
          isRequired: true,
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionTwo.costCoverage,
        },
      },
      site_id_number: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isDisabled
            isRequired={!skipRequiredValidation}
            label={translations.careseeker.siteIdNumber}
            name="site_id_number"
          />
        ),
        position: 1,
      },
      social_worker_fax_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.generalForm.sectionTwo.socialWorkerFax
            }
            name="social_worker_fax_number"
            type="tel"
          />
        ),
        position: 11,
      },
      social_worker_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.firstName}
            name="social_worker_first_name"
          />
        ),
        position: 8,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.generalForm.sectionTwo
              .detailsSocialWorker,
        },
      },
      social_worker_last_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.people.lastName}
            name="social_worker_last_name"
          />
        ),
        position: 9,
      },
      social_worker_phone_number: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionTwo.socialWorkerPhone
            }
            name="social_worker_phone_number"
            type="tel"
          />
        ),
        position: 10,
      },
    },
    position: 5,
    title: ({ translations }) =>
      translations.patientForms.generalForm.sectionTwo.title,
  },
];
